import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MoviePage.module.scss'
import MovieService from '../../../service/MovieService'
import { MovieIcon } from 'components/CustomIcons'
import MovieCard from './components/MovieCard/MovieCard'
import MovieFilters from './components/MovieFilters/MovieFilters'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import SearchBar from './components/SearchBar/SearchBar'
import GenreFilter from './components/GenreFilter/GenreFilter'
import { addItemToPanel, getMyPanel } from '../../../service/PersonalPanelService'

const MoviePage = ({ embedded = false, goBack }) => {
  const navigate = useNavigate()
  const [movies, setMovies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState({
    genre: '',
    year: '',
    rating: '',
    search: ''
  })
  const [genres, setGenres] = useState([])
  const [sortBy, setSortBy] = useState('rating')
  const [viewMode, setViewMode] = useState('grid') // grid, list
  const [favorites, setFavorites] = useState([])
  const [panelMovieIds, setPanelMovieIds] = useState(new Set())

  useEffect(() => {
    loadMovies()
    loadGenres()
    loadFavorites()
    loadPanelMovieIds()
  }, [])

  const loadPanelMovieIds = async () => {
    if (!localStorage.getItem('token')) return
    try {
      const response = await getMyPanel()
      const items = response.data?.items || []
      const ids = items
        .filter((item) => item.item_type === 'movie')
        .map((item) => String(item.item_id))
      setPanelMovieIds(new Set(ids))
    } catch (error) {
      console.error('Error loading movie panel ids:', error)
    }
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [filters, sortBy])

  const loadMovies = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await MovieService.getMovies({
        page_size: 1000,
        sort_by: 'rating'
      })
      setMovies(response.results || [])
    } catch (err) {
      setError('Ошибка загрузки фильмов')
      setRetryAction(() => loadMovies)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const filteredMovies = useMemo(() => {
    let filtered = movies.filter(movie => {
      // Поиск по названию
      if (filters.search && !(movie.name || movie.title || '').toLowerCase().includes(filters.search.toLowerCase())) return false
      
      // Фильтр по жанру (проверяем массив categories)
      if (filters.genre && !movie.categories?.includes(parseInt(filters.genre))) return false
      
      // Фильтр по году (извлекаем год из release_date)
      if (filters.year) {
        const movieYear = movie.release_date ? new Date(movie.release_date).getFullYear() : null
        if (movieYear !== parseInt(filters.year)) return false
      }
      
      // Фильтр по минимальному рейтингу
      if (filters.rating) {
        const movieRating = parseFloat(movie.rating_avg) || 0
        if (movieRating < parseFloat(filters.rating)) return false
      }
      
      return true
    })

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating': return (parseFloat(b.rating_avg) || 0) - (parseFloat(a.rating_avg) || 0)
        case 'year': {
          const yearA = a.release_date ? new Date(a.release_date).getFullYear() : 0
          const yearB = b.release_date ? new Date(b.release_date).getFullYear() : 0
          return yearB - yearA
        }
        case 'title': return ((a.name || a.title) || '').localeCompare((b.name || b.title) || '')
        case 'popularity': return (b.likes_count || 0) - (a.likes_count || 0)
        default: return 0
      }
    })

    return filtered
  }, [movies, filters, sortBy])

  const paginatedMovies = useMemo(() => {
    const start = (currentPage - 1) * 20
    return filteredMovies.slice(start, start + 20)
  }, [filteredMovies, currentPage])

  const totalPages = Math.ceil(filteredMovies.length / 20)

  const loadGenres = async () => {
    try {
      const response = await MovieService.getGenres()
      setGenres(response)
    } catch (err) {
      console.error('Ошибка загрузки жанров:', err)
    }
  }

  const loadFavorites = async () => {
    try {
      const response = await MovieService.getFavorites()
      setFavorites(response.map(item => item.slug))
    } catch (err) {
      console.error('Ошибка загрузки избранного:', err)
    }
  }

  const handleMovieClick = (movie) => {
    navigate(`/services/movie/watch/${movie.slug || movie.id}`, {
      state: {
        movie
      }
    })
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSortChange = (newSort) => {
    setSortBy(newSort)
    setCurrentPage(1)
  }

  const toggleFavorite = async (movieSlug) => {
    try {
      if (favorites.includes(movieSlug)) {
        await MovieService.removeFromFavorites(movieSlug)
        setFavorites(prev => prev.filter(slug => slug !== movieSlug))
      } else {
        await MovieService.addToFavorites(movieSlug)
        setFavorites(prev => [...prev, movieSlug])
      }
    } catch (err) {
      console.error('Ошибка изменения избранного:', err)
    }
  }

  const handleRating = async (movieSlug, rating) => {
    try {
      await MovieService.rateMovie(movieSlug, rating)
      // Обновляем рейтинг в текущем списке
      setMovies(prev => prev.map(movie => 
        movie.slug === movieSlug 
          ? { ...movie, user_rating: rating }
          : movie
      ))
    } catch (err) {
      console.error('Ошибка оценки фильма:', err)
    }
  }

  const handleAddMovieToPanel = async (movie) => {
    if (!localStorage.getItem('token')) {
      window.dispatchEvent(new CustomEvent('openAuth', { detail: true }))
      return
    }

    try {
      const panelId = String(movie.slug || movie.id)
      await addItemToPanel('movie', panelId, {
        title: movie.name || movie.title,
        poster: movie.poster,
        description: movie.description || '',
        rating: movie.rating_avg || movie.rating || null,
        type: movie.type || null
      })
      setPanelMovieIds((prev) => new Set(prev).add(panelId))
    } catch (error) {
      if (error?.response?.status === 400) {
        setPanelMovieIds((prev) => new Set(prev).add(String(movie.slug || movie.id)))
      } else {
        console.error('Error adding movie to panel:', error)
      }
    }
  }

  if (loading && movies.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка фильмов...</p>
      </div>
    )
  }

  return (
    <div className={styles.moviePage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <MovieIcon className={styles.icon} size={32} />
            База данных фильмов
          </h1>
          <p className={styles.pageSubtitle}>
            Откройте для себя лучшие фильмы всех времен
          </p>
        </div>
      )}

      <div className={styles.controls}>
        <div className={styles.topControls}>
          <SearchBar onSearch={handleSearch} />
          
          <div className={styles.viewControls}>
            <div className={styles.sortSelect}>
              <select 
                value={sortBy} 
                onChange={(e) => handleSortChange(e.target.value)}
                className={styles.select}
              >
                <option value="rating">По рейтингу</option>
                <option value="year">По году</option>
                <option value="title">По названию</option>
                <option value="popularity">По популярности</option>
              </select>
            </div>

            <div className={styles.viewMode}>
              <button 
                className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
              <button 
                className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
                onClick={() => setViewMode('list')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className={styles.filters}>
          <GenreFilter 
            genres={genres}
            selectedGenre={filters.genre}
            onGenreChange={(genre) => handleFilterChange({ ...filters, genre })}
          />
          
          <MovieFilters 
            filters={filters}
            onFiltersChange={handleFilterChange}
          />
        </div>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <MovieIcon className={styles.errorIcon} size={48} />
          <div className={styles.errorContent}>
            <h3 className={styles.errorTitle}>Ошибка загрузки фильмов</h3>
            <p className={styles.errorMessage}>{error}</p>
            <button 
              onClick={handleRetry}
              className={styles.retryButton}
              disabled={loading}
            >
              {loading ? 'Загрузка...' : 'Повторить попытку'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.stats}>
          <span>Найдено фильмов: <strong>{filteredMovies.length}</strong></span>
        </div>

        <div className={`${styles.moviesContainer} ${styles[viewMode]}`}>
          {paginatedMovies.map(movie => (
            <MovieCard
              key={movie.id}
              movie={movie}
              onClick={() => handleMovieClick(movie)}
              onToggleFavorite={() => toggleFavorite(movie.slug)}
              onRate={(rating) => handleRating(movie.slug, rating)}
              onAddToPanel={() => handleAddMovieToPanel(movie)}
              isInPanel={panelMovieIds.has(String(movie.slug || movie.id))}
              isFavorite={favorites.includes(movie.slug)}
              viewMode={viewMode}
            />
          ))}
        </div>

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

    </div>
  )
}

export default MoviePage